import { create } from 'zustand';
import { getAuth } from '../api/generated/auth/auth';
import type { FacilityFlowCoreModulesEmployeesDtosEmployeeDto } from '../api/generated/models';
import { useStampingWindowStore } from './useStampingWindowStore';

interface AuthState {
  employee: FacilityFlowCoreModulesEmployeesDtosEmployeeDto | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  isSuperAdmin: boolean;
  isAdmin: boolean; // SuperAdmin oder Verwaltung
}

interface AuthStore extends AuthState {
  login: (identifier: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  fetchCurrentUser: () => Promise<void>;
  forgotPassword: (personalNumber: string, email?: string) => Promise<void>;
  resetPassword: (personalNumber: string, resetToken: string, newPassword: string) => Promise<void>;
  clearError: () => void;
}

const {
  postApiV1AuthLogin,
  postApiV1AuthLogout,
  getApiV1AuthMe,
  postApiV1AuthForgotPassword,
  postApiV1AuthResetPassword,
} = getAuth();

// Helper functions for manual persistence
const getStoredAuth = () => {
  try {
    const stored = localStorage.getItem('auth-storage');
    if (stored) {
      const parsed = JSON.parse(stored);
      return parsed.state || {};
    }
  } catch (e) {
    console.error('Error loading auth from storage:', e);
  }
  return {};
};

const saveAuthToStorage = (state: Pick<AuthState, 'employee' | 'isAuthenticated' | 'isSuperAdmin' | 'isAdmin'>) => {
  try {
    localStorage.setItem('auth-storage', JSON.stringify({ state }));
  } catch (e) {
    console.error('Error saving auth to storage:', e);
  }
};

export const useAuthStore = create<AuthStore>((set) => {
  const stored = getStoredAuth();

  return {
    employee: stored.employee || null,
    isAuthenticated: stored.isAuthenticated || false,
    isLoading: false,
    error: null,
    isSuperAdmin: stored.isSuperAdmin || false,
    isAdmin: stored.isAdmin || false,

    login: async (identifier: string, password: string) => {
      set({ isLoading: true, error: null });
      try {
        await postApiV1AuthLogin({
          identifier,
          password,
        });

        // Nach erfolgreichem Login: Benutzerinfo von /api/v1/auth/me abrufen
        const employee = await getApiV1AuthMe();
        const roleLower = employee?.role?.toLowerCase() || '';
        const isSuperAdmin = roleLower === 'superadmin';
        const isAdmin = isSuperAdmin || roleLower === 'verwaltung';

        if (employee?.tenantId) {
          localStorage.setItem('tenantId', employee.tenantId);
        }

        const newState = {
          employee: employee || null,
          isAuthenticated: true,
          isSuperAdmin,
          isAdmin,
        };

        set({
          ...newState,
          isLoading: false,
          error: null,
        });

        // Persist to localStorage
        saveAuthToStorage(newState);
      } catch (err) {
        set({
          isLoading: false,
          error: 'Anmeldung fehlgeschlagen. Bitte prüfen Sie Personalnummer oder E-Mail und Passwort.',
        });
        throw err;
      }
    },

    logout: async () => {
      try {
        await postApiV1AuthLogout();
      } catch (err) {
        console.error('Logout error:', err);
      } finally {
        set({
          employee: null,
          isAuthenticated: false,
          error: null,
          isSuperAdmin: false,
          isAdmin: false,
        });
        // why: the clock-in window is per user; the next login must not see this one's.
        useStampingWindowStore.getState().reset();
        // Clear from localStorage
        localStorage.removeItem('auth-storage');
        localStorage.removeItem('tenantId');
      }
    },

    checkAuth: async () => {
      try {
        const employee = await getApiV1AuthMe();
        const roleLower = employee?.role?.toLowerCase() || '';
        const isSuperAdmin = roleLower === 'superadmin';
        const isAdmin = isSuperAdmin || roleLower === 'verwaltung';
        const newState = {
          employee: employee || null,
          isAuthenticated: true,
          isSuperAdmin,
          isAdmin,
        };
        set(newState);
        saveAuthToStorage(newState);
      } catch (error) {
        // Auth check failed - SOFORT State bereinigen
        set({
          employee: null,
          isAuthenticated: false,
          isSuperAdmin: false,
          isAdmin: false,
        });
        localStorage.removeItem('auth-storage');
        // Fehler weiterwerfen für besseres Error Handling
        throw error;
      }
    },

    fetchCurrentUser: async () => {
      try {
        const employee = await getApiV1AuthMe();
        const roleLower = employee?.role?.toLowerCase() || '';
        const isSuperAdmin = roleLower === 'superadmin';
        const isAdmin = isSuperAdmin || roleLower === 'verwaltung';
        const newState = {
          employee: employee || null,
          isAuthenticated: true,
          isSuperAdmin,
          isAdmin,
        };
        set(newState);
        saveAuthToStorage(newState);
      } catch (err) {
        console.error('Failed to fetch current user:', err);
        // Don't clear auth state on error, user might still be authenticated
      }
    },

    forgotPassword: async (personalNumber: string) => {
      // Backend always responds generically (enumeration protection); only the
      // request transport can fail here.
      // why: ForgotPasswordRequest only carries the personal number — the reset link
      // is sent to the employee's stored e-mail (the interface keeps an optional email
      // arg for call-site compatibility, but it is not part of the API contract).
      await postApiV1AuthForgotPassword({
        personalNumber,
      });
    },

    resetPassword: async (personalNumber: string, resetToken: string, newPassword: string) => {
      await postApiV1AuthResetPassword({
        personalNumber,
        resetToken,
        newPassword,
      });
    },

    clearError: () => set({ error: null }),
  };
});
